import { useMemo } from "react";
import { Home, TreePine, Sparkles } from "lucide-react";

/**
 * iter127 — Yard Area picker (matches the screenshot: 3 premium cards).
 *
 * Design is intentionally simple, per customer feedback:
 *   • Front Yard      — "Just the front"
 *   • Back Yard       — "Just the back"
 *   • Front + Back    — "Whole yard" · "FULL" pill on the right
 *
 * The dark selected card matches the estimate CTA aesthetic. No multiplier
 * text is shown — the pricing engine consumes the `areas` list and quietly
 * applies the correct labor multiplier.
 *
 * Props:
 *   value      — canonical areas array, one of:
 *                  ["front_yard"] · ["back_yard"] · ["front_yard","back_yard"]
 *   onChange   — (nextArray) => void
 *   disabled   — bool
 *
 * Constants exported for other components that need to encode/decode
 * the shared `areas` shape.
 */
export const AREA_KEYS = ["front_yard", "back_yard"];  // only 2 quadrants exposed to customers now
export const AREA_LABELS = {
  front_yard: "Front Yard",
  back_yard:  "Back Yard",
};

// Legacy compatibility export — some callers (Extra Work quote-only) still
// import `areaMultiplierFor` to gate their submit button. With only 3 UI
// options this is essentially a no-op indicator (1 → 1.0, 2 → 1.75 for
// area-based services), and the backend is authoritative.
export function areaMultiplierFor(list) {
  const canonical = Array.isArray(list) ? list : [];
  const uniq = new Set(canonical);
  if (uniq.size >= 2) return 1.75;
  return 1.0;
}

export function normalizeAreas(list) {
  const arr = Array.isArray(list) ? list.map((s) => String(s).toLowerCase()) : [];
  const front = arr.includes("front_yard");
  const back = arr.includes("back_yard");
  if (front && back) return ["front_yard", "back_yard"];
  if (front) return ["front_yard"];
  if (back) return ["back_yard"];
  return [];
}

/** The three visible options, in the exact order + copy from the design. */
const OPTIONS = [
  {
    key: "front",
    value: ["front_yard"],
    label: "Front Yard",
    sub: "Just the front",
    Icon: Home,
  },
  {
    key: "back",
    value: ["back_yard"],
    label: "Back Yard",
    sub: "Just the back",
    Icon: TreePine,
  },
  {
    key: "both",
    value: ["front_yard", "back_yard"],
    label: "Front + Back",
    sub: "Whole yard",
    Icon: Sparkles,
    badge: "FULL",
  },
];

/** Encode the current `areas` list to one of "front"/"back"/"both"/null. */
function encodeSelection(value) {
  const arr = normalizeAreas(value);
  if (arr.length === 2) return "both";
  if (arr[0] === "front_yard") return "front";
  if (arr[0] === "back_yard") return "back";
  return null;
}

export default function AreaSelector({ value, onChange, disabled = false, showHeader = true }) {
  const active = useMemo(() => encodeSelection(value), [value]);

  return (
    <div className="w-full" data-testid="area-selector">
      {showHeader && (
        <div className="text-[10px] uppercase tracking-[0.22em] text-gray-500 font-bold mb-3">
          Yard Area
        </div>
      )}
      <div className="grid grid-cols-3 gap-2.5">
        {OPTIONS.map((o) => {
          const isActive = active === o.key;
          const cardClasses = isActive
            ? "bg-gradient-to-br from-[#0A2E1E] to-[#062818] text-white border-transparent shadow-md shadow-black/10"
            : "bg-white text-gray-800 border-gray-200 hover:border-gray-300";
          const badgeClasses = isActive
            ? "bg-white/15 text-emerald-100"
            : "bg-gray-100 text-gray-600";
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => onChange(o.value)}
              disabled={disabled}
              aria-pressed={isActive}
              data-testid={`yard-${o.key}`}
              className={
                "relative rounded-2xl border p-3 transition-all active:scale-[0.98] text-left disabled:opacity-60 disabled:cursor-not-allowed " +
                cardClasses
              }
            >
              <div className="flex items-center justify-between gap-2">
                <o.Icon className={`w-4 h-4 ${isActive ? "text-emerald-300" : "text-[#10B981]"}`} />
                {o.badge && (
                  <span className={`text-[9px] font-extrabold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full ${badgeClasses}`}>
                    {o.badge}
                  </span>
                )}
              </div>
              <div className="mt-2 text-[13px] font-extrabold tracking-tight font-[Outfit] leading-tight">
                {o.label}
              </div>
              <div className={`mt-0.5 text-[11px] ${isActive ? "text-emerald-100/80" : "text-gray-500"}`}>
                {o.sub}
              </div>
              {isActive && (
                <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-emerald-400 via-emerald-300 to-emerald-400 rounded-b-2xl" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
